/**
 * AddonsContext — cached read of the workspace's add-on entitlements.
 *
 * Today only tracks the Enterprise Workforce Suite. Extend the shape
 * later (returning a map keyed by addon_key) if more add-ons are added.
 *
 * The state is refreshed:
 *   * once on mount (after auth resolves)
 *   * after a successful purchase (call refresh())
 *
 * Non-admin users still hit the endpoint but get 403 — we swallow the
 * error and default `hasEnterpriseWorkforce` to false so downstream nav
 * gating is safe.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AddonsContext = createContext({
  loading: false,
  hasEnterpriseWorkforce: false,
  enterpriseWorkforce: null,
  refresh: async () => {},
});

export const AddonsProvider = ({ children }) => {
  const { user } = useAuth();
  const [enterpriseWorkforce, setEnterpriseWorkforce] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setEnterpriseWorkforce(null); return; }
    setLoading(true);
    const roles = user.roles || [];
    const isAdmin = roles.includes("owner") || roles.includes("admin");
    try {
      if (isAdmin) {
        const { data } = await api.get("/addons/enterprise-workforce");
        setEnterpriseWorkforce(data);
      } else {
        const { data } = await api.get("/addons/enterprise-workforce/status");
        // Shape the response like the admin one so consumers only need
        // to read `.state.status` regardless of who's logged in.
        setEnterpriseWorkforce({
          addon_key: data.addon_key,
          state: { status: data.status, active: data.active },
        });
      }
    } catch {
      setEnterpriseWorkforce({ state: { status: "unknown" } });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({
    loading,
    enterpriseWorkforce,
    hasEnterpriseWorkforce: enterpriseWorkforce?.state?.status === "active",
    refresh,
  }), [loading, enterpriseWorkforce, refresh]);

  return <AddonsContext.Provider value={value}>{children}</AddonsContext.Provider>;
};

export const useAddons = () => useContext(AddonsContext);
